import type { TvChannel, TvShowEpisode, Vod } from '@setplex/tria-api'
import type React from 'react'
import type { ReactNode } from 'react'
import type { PlayingEvent } from '~/shared/interfaces'
import type { UsePlayer } from '~/widgets/Player/hooks/usePlayer'

import { useEffect, useMemo } from 'react'

export const PLAYER_VIEWS = {
  geoLock: 'geoLock',
  signInToWatch: 'signInToWatch',
  pinLock: 'pinLock',
  pinLockPayment: 'pinLockPayment',
  payment: 'payment',
  goLive: 'goLive',
} as const

export type ViewsUnionType = (typeof PLAYER_VIEWS)[keyof typeof PLAYER_VIEWS]

const VIEWS_PRIORITY: ViewsUnionType[] = [
  'geoLock',
  'signInToWatch',
  'pinLock',
  'pinLockPayment',
  'payment',
  'goLive',
]

export type Views =
  | Partial<Record<ViewsUnionType, undefined | ReactNode>>
  | undefined

// 'blockedByAcl' may be missed in some - but it is not a problem
type TContent = (TvChannel | Vod | PlayingEvent | TvShowEpisode) & {
  blockedByAcl?: boolean
}

type PlayerViewsProps = {
  player?: UsePlayer
  views: Views
  content?: TContent | null | undefined
  pinLocked?: boolean | null
  pinLockPayment?: boolean | null
  showSignInToWatchPopup?: boolean | null
  showPaymentPopup?: boolean | null
  showGoLivePopup?: boolean | null
  onViewChanged?: (view: ViewsUnionType | undefined) => void
  onSafeChanged?: (isSafe: boolean) => void
}

export const PlayerOrderedViews: React.FC<PlayerViewsProps> = ({
  player,
  views,
  content,
  pinLocked,
  showSignInToWatchPopup,
  showPaymentPopup,
  showGoLivePopup,
  onViewChanged,
  onSafeChanged,
  pinLockPayment,
}) => {
  const { blockedByAcl } = content ?? { blockedByAcl: false }

  const pairs: Record<ViewsUnionType, boolean | null | undefined> = useMemo(
    () => ({
      geoLock: blockedByAcl,
      signInToWatch: showSignInToWatchPopup,
      pinLock: pinLocked,
      pinLockPayment: pinLockPayment,
      payment: showPaymentPopup,
      goLive: showGoLivePopup,
    }),
    [
      blockedByAcl,
      showSignInToWatchPopup,
      pinLocked,
      pinLockPayment,
      showPaymentPopup,
      showGoLivePopup,
    ]
  )

  const state = useMemo(() => {
    if (!views || Object.keys(views).length === 0) return

    // check if any required data is not ready
    const isNotReady = VIEWS_PRIORITY.some(
      (key) => typeof pairs[key] !== 'boolean' && key in views
    )
    if (isNotReady) return

    // state function: returns the value for a given view
    return (view: ViewsUnionType) => (views?.[view] ? pairs[view] : false)
  }, [pairs, views])

  const activeView = useMemo(
    () => VIEWS_PRIORITY.find((priorityView) => Boolean(state?.(priorityView))),
    [state]
  )

  useEffect(() => {
    if (activeView) {
      player?.instance?.addClass('vjs-x-player-disabled-playing')
    } else {
      player?.instance?.removeClass('vjs-x-player-disabled-playing')
    }
  }, [activeView, player])

  useEffect(() => {
    onViewChanged?.(activeView)
    onSafeChanged?.(Boolean(state))
  }, [state, activeView, onViewChanged, onSafeChanged])

  return activeView ? (views?.[activeView] ?? null) : null
}
